/**
 * LrclibService
 *
 * Fetches synced lyrics from https://lrclib.net — a free, open-source lyrics
 * database with no authentication and no CORS restrictions.
 *
 * Converts the LRC-format `syncedLyrics` string returned by the API into the
 * same `TranscriptSegment[]` shape used by the rest of the app, so the existing
 * lyrics panel and translation flow work unchanged.
 *
 * Returns null (not throws) when no synced lyrics are found — callers fall back
 * to YouTube captions.
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranscriptSegment } from './transcript.service';

interface LrclibResponse {
  id: number;
  trackName: string;
  artistName: string;
  albumName: string;
  duration: number;
  instrumental: boolean;
  plainLyrics: string | null;
  syncedLyrics: string | null;
}

@Injectable({ providedIn: 'root' })
export class LrclibService {
  private http = inject(HttpClient);

  /**
   * Fetches synced lyrics for a track and returns them as TranscriptSegment[].
   *
   * Strategy:
   *  1. Try /api/get (exact/fuzzy match by track + artist + optional duration)
   *  2. If not found, fall back to /api/search?q=... and pick the first result
   *     with synced lyrics — handles non-Latin titles, alternate spellings, etc.
   *
   * @param trackName  Song title (from YouTube oEmbed / Spotify search)
   * @param artistName Artist / channel name
   * @param durationSeconds  Optional track length — improves match accuracy
   * @returns Parsed segments, or null if no synced lyrics found / request fails
   */
  async getSegments(
    trackName: string,
    artistName: string,
    durationSeconds?: number
  ): Promise<TranscriptSegment[] | null> {
    if (!trackName.trim()) return null;

    // ── 1. Exact/fuzzy get ────────────────────────────────────────────────────
    try {
      const params = new URLSearchParams({ track_name: trackName.trim() });
      if (artistName.trim()) params.set('artist_name', artistName.trim());
      if (durationSeconds && durationSeconds > 0) {
        params.set('duration', Math.round(durationSeconds).toString());
      }

      const data = await firstValueFrom(
        this.http.get<LrclibResponse>(
          `https://lrclib.net/api/get?${params.toString()}`
        )
      );

      if (data && !data.instrumental && data.syncedLyrics) {
        const segments = this.parseLrc(data.syncedLyrics);
        if (segments.length) return segments;
      }
    } catch { /* 404 or network error — fall through to search */ }

    // ── 2. Search fallback ────────────────────────────────────────────────────
    try {
      const q = artistName.trim()
        ? `${trackName.trim()} ${artistName.trim()}`
        : trackName.trim();

      const results = await firstValueFrom(
        this.http.get<LrclibResponse[]>(
          `https://lrclib.net/api/search?q=${encodeURIComponent(q)}`
        )
      );

      if (results?.length) {
        for (const r of results) {
          if (!r.instrumental && r.syncedLyrics) {
            const segments = this.parseLrc(r.syncedLyrics);
            if (segments.length) return segments;
          }
        }
      }
    } catch { /* search also failed */ }

    return null;
  }

  /**
   * Parses an LRC-formatted string into TranscriptSegment[].
   *
   * LRC format:  [mm:ss.xx] lyrics text
   * Example:     [01:23.45] Hello, world
   *
   * Duration for each segment is derived from the next segment's start time.
   * The last segment gets a fixed 5-second duration.
   */
  parseLrc(lrc: string): TranscriptSegment[] {
    // Matches [mm:ss.xx] or [mm:ss.xxx] with optional trailing text
    const timeRegex = /^\[(\d{1,2}):(\d{2})\.(\d{2,3})\]\s*(.*)/;
    const segments: TranscriptSegment[] = [];

    for (const line of lrc.split('\n')) {
      const match = line.trim().match(timeRegex);
      if (!match) continue;
      const [, mm, ss, msRaw, text] = match;
      const ms = parseInt(msRaw.padEnd(3, '0'), 10); // normalise 2 or 3 digit ms
      const start = parseInt(mm, 10) * 60 + parseInt(ss, 10) + ms / 1000;
      const words = text.trim();
      // Skip empty lines (e.g. instrumental markers)
      if (words) {
        segments.push({ text: words, start, duration: 0 });
      }
    }

    // Fill durations from successive start times
    for (let i = 0; i < segments.length - 1; i++) {
      segments[i].duration = segments[i + 1].start - segments[i].start;
    }
    if (segments.length > 0) {
      segments[segments.length - 1].duration = 5;
    }

    return segments;
  }
}
